import React, { useContext, useState } from 'react';

import { StyleSheet, View, TouchableHighlight } from 'react-native';
import DyteClient from '@dytesdk/web-core';
import UserAvatar from 'react-native-user-avatar';
import { defaultIconPack, IconPack } from '../lib/icons';
import { Poll, PollOption } from '../types/props';
import { DyteI18n, useLanguage } from '../lib/lang';
import DyteText from './DyteText';
import { useDyteSelector } from '@dytesdk/react-native-core/lib/ReactHooks';
import { SvgXml } from 'react-native-svg';
import { DyteUIContext } from '../lib/context-provider/ui-context';

function PollOptionView({
  option,
  selfId,
  onDyteVotePoll,
}: {
  poll: Poll;
  option: PollOption;
  selfId: string;
  onDyteVotePoll: any;
}) {
  const { storeStates } = useContext(DyteUIContext);
  const { colors } = storeStates.designSystem;
  const selfVote = option.votes.find((v) => v.id === selfId);
  const [update, setUpdate] = useState(false);
  const styles = StyleSheet.create({
    voteContainer: {
      backgroundColor: selfVote ? colors.brand[500] : colors.background[700],
      borderRadius: 8,
      padding: 6,
    },
    checkBoxContainer: {
      flexDirection: 'row',
      paddingHorizontal: 1,
      alignItems: 'center',
      justifyContent: 'space-between',
      marginRight: 6,
    },
    avatarContainer: {
      flexDirection: 'row',
      marginTop: 1,
    },
    avatar: {
      marginRight: 5,
      marginTop: 5,
      marginBottom: 5,
    },
    iconStyle: {
      height: 20,
      width: 20,
      marginRight: 4,
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: colors.background[1000],
      borderRadius: 8,
    },
  });
  return (
    <View style={styles.voteContainer}>
      <View style={styles.checkBoxContainer}>
        <View
          // eslint-disable-next-line react-native/no-inline-styles
          style={{
            flex: 1,
            flexDirection: 'row',
            alignItems: 'center',
            marginRight: 25,
          }}
        >
          <TouchableHighlight
            underlayColor={colors.background[800]}
            onPress={() => {
              onDyteVotePoll();
              setUpdate(!update);
            }}
            style={styles.iconStyle}
          >
            {selfVote ? (
              <SvgXml
                xml={defaultIconPack.checkmark.replace(
                  'currentColor',
                  colors.success
                )}
                width={16}
                height={16}
              />
            ) : (
              <View />
            )}
          </TouchableHighlight>
          <DyteText>{option.text}</DyteText>
        </View>
        {option.count !== null && <DyteText>{`(${option.count})`}</DyteText>}
      </View>
      <View style={styles.avatarContainer}>
        {option.votes.map((vote, index) => (
          <UserAvatar
            key={index}
            style={styles.avatar}
            bgColor={selfVote ? colors.background[700] : colors.brand[500]}
            color={colors.text}
            name={vote.name}
          />
        ))}
      </View>
    </View>
  );
}

function PollsCard({
  poll,
  onDyteVotePoll,
  meeting,
}: {
  poll: Poll;
  onDyteVotePoll: any;
  meeting: DyteClient;
}) {
  const { storeStates } = useContext(DyteUIContext);
  const { colors } = storeStates.designSystem;
  const options = useDyteSelector(
    (m) => m.polls.items.find((p) => p.id === poll.id).options
  );
  const styles = StyleSheet.create({
    pollsCardContainer: {
      spacing: 2,
      width: '60%',
      flexDirection: 'column',
      backgroundColor: colors.background[800],
      padding: 12,
      margin: 10,
      borderRadius: 20,
    },
  });
  return (
    <View style={styles.pollsCardContainer}>
      {/* eslint-disable-next-line react-native/no-inline-styles */}
      <DyteText style={{ textAlign: 'left' }}>{poll.question}</DyteText>
      {/* eslint-disable-next-line react-native/no-inline-styles */}
      <View style={{ marginTop: 8 }}>
        {options.map((option, index) => (
          <TouchableHighlight
            underlayColor={colors.background[800]}
            disabled={!meeting.self.permissions.polls.canVote}
            // eslint-disable-next-line react-native/no-inline-styles
            style={{ marginBottom: 4 }}
            key={index}
          >
            <PollOptionView
              poll={poll}
              option={option}
              selfId={meeting.self.userId}
              onDyteVotePoll={() => {
                meeting.self.permissions.polls.canVote && onDyteVotePoll(index);
              }}
            />
          </TouchableHighlight>
        ))}
      </View>
    </View>
  );
}

export default function ({
  poll,
  onDyteVotePoll,
  iconPack = defaultIconPack,
  meeting,
  t,
}: {
  poll: Poll;
  onDyteVotePoll?: any;
  self?: string;
  iconPack?: IconPack;
  meeting: DyteClient;
  t?: DyteI18n;
}) {
  const defaultLang = useLanguage();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  t = t ?? defaultLang;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  iconPack = iconPack ?? defaultIconPack;
  return (
    <PollsCard meeting={meeting} poll={poll} onDyteVotePoll={onDyteVotePoll} />
  );
}
